
import Vue from "vue";
import { Component, Inject, Prop } from "vue-property-decorator";
import { ControlsTreeContext, DropdownOptions } from "@/types/controls";
import * as t from "io-ts";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import { fetchOptionData } from "@/lib/wordpress/data";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import { OnPropertyChange } from "@/types/store-actions";
import { filterItem, mapItem } from "@/lib/controls/populate";
import lodash from "lodash";
import { documentStore } from "@/store/store";
import { VAutocomplete, VSelect } from "vuetify/lib";
import Icon from "@/components/UI/Icon.vue";

@Component({
  components: { Icon }
})
export default class DropdownInput extends Vue {
  @Prop()
  value!: string;

  @Prop({ default: false })
  autocomplete!: boolean;

  get dropdownType() {
    return this.autocomplete ? VAutocomplete : VSelect;
  }

  @Prop({
    default: false
  })
  clearable!: boolean;

  @Prop({
    default: false
  })
  disabled!: boolean;

  @Prop()
  placeholder?: string;

  @Prop()
  items!: [{ text: string; value: string; subLabel?: string }] | unknown; // Unknown is here because this type is probably a lie. Sometimes DropdownInput.items probably just gets an array of strings.

  @Prop()
  dropdownOptions!: DropdownOptions;

  @Prop()
  path!: string;

  @Inject({ from: "context", default: null })
  readonly context!: ControlsTreeContext | null;

  created() {
    if (
      this.dropdownOptions?.populate?.fetchDataAction &&
      this.retrievedItems.length === 0
    ) {
      this.retrieveItems();
    }

    breakdanceStoreActions.register.onPropertyChange(this.retrieveItemsAction);
  }

  retrieveItemsAction({ path }: OnPropertyChange) {
    if (this.loadingOptionData || !this.context) {
      return;
    }
    const refetchPathArray = this.dropdownOptions?.populate?.refetchPaths ?? [];

    const refetchPaths = refetchPathArray.map(refetchPath => {
      return this.processVariablesInPath(path, refetchPath);
    });

    if (refetchPaths && refetchPaths.includes(path)) {
      this.retrieveItems();
    }
  }

  processVariablesInPath(path: string, pathToReplace: string) {
    // Remove the last control in the path
    const currentPath = path.split(".");
    currentPath.pop();
    const joinedCurrentPath = currentPath.join(".");

    return pathToReplace.replace("%%CURRENTPATH%%", joinedCurrentPath);
  }

  beforeDestroy() {
    breakdanceStoreActions.unregister(
      "onPropertyChange",
      this.retrieveItemsAction
    );
  }

  get internalItems() {
    if (this.populate.fetchDataAction && this.retrievedItems.length) {
      return this.retrievedItems;
    }

    return this.mappedItems.length ? this.mappedItems : this.items;
  }

  get populate() {
    return this.dropdownOptions?.populate || {};
  }

  get placeholderText() {
    if (this.loadingOptionData) {
      return "Loading...";
    }

    if (
      !this.internalItems ||
      (Array.isArray(this.internalItems) && !this.internalItems.length)
    ) {
      return (
        this.dropdownOptions?.noDataAvailableMessage || "No data available"
      );
    }

    return this.placeholder;
  }

  get mappedItems() {
    return this.populatedItems
      .map(item => {
        return mapItem(this.populate, item);
      }) // Turn array into the `v-select` expected input
      .filter(Boolean); // Filter out falsy values
  }

  get populatedItems() {
    if (!this.populate || !this.populate.path) {
      return [];
    }

    if (!this.context) {
      console.log(`[DROPDOWN] Context is not available to populate dropdown.`);
      return [];
    }

    const controlValue = this.context.getControlValue(this.populate.path);

    return pipe(
      controlValue,
      t.array(t.unknown).decode,
      fold(
        () => {
          console.log(
            `[DROPDOWN] Populate path '${this.populate?.path}' is invalid.`
          );

          return [];
        },
        unknownArray => {
          return unknownArray.filter(item => filterItem(this.populate, item)); // Filter out items based on condition
        }
      )
    );
  }

  get retrieve() {
    return this.dropdownOptions?.populate?.fetchDataAction || {};
  }

  loadingOptionData = false;
  retrievedItems: { text: string; value: string }[] = [];
  async retrieveItems() {
    const action = this.dropdownOptions?.populate?.fetchDataAction;

    if (!action) {
      return;
    }

    const postId = documentStore.document.documentMeta.id;

    let contextData: unknown = {
      postId
    };

    if (this.context && this.dropdownOptions?.populate?.fetchContextPath) {
      const fetchContextPath = this.processVariablesInPath(
        this.path,
        this.dropdownOptions.populate.fetchContextPath
      );
      contextData = this.context.getControlValue(fetchContextPath) || {};
    }

    this.loadingOptionData = true;

    this.retrievedItems = await fetchOptionData(action, {
      context: contextData
    });

    this.loadingOptionData = false;
  }

  edit(string: unknown) {
    if (!string) {
      string = "";
    }

    this.$emit("input", string);

    const selectedItem = this.populatedItems.find((item: any) => {
      const valueIndex = this.populate.value ?? "label";
      const value = lodash.get(item, valueIndex);
      return value === string;
    });
    this.$emit("selected", selectedItem);
  }
}
